import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Link, useLocation } from 'react-router-dom';
import { AvailableHotelModel } from '@/__generated';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { HOTEL_CONFIG } from '@/config';
import HotelStar from './HotelStar';
import { generateHotelDetailUrl, getHotelSearchRequest } from '@/urls';
import HotelJapaneseName from './HotelJapaneseName';
import { HotelServicesAndConveniences } from '@/pages/HotelDetail/type';
import moment from 'moment';
import BoardTypeList from './BoardTypeList';
import HotelBookmarkIcon from './HotelBookmarkIcon';
import FacilityList from './FacilityList';
import { Navigation } from 'swiper';
import HotelRewardRating from '../HotelRewardRating';

type Props = {
  hotel: AvailableHotelModel;
  bookmarkHotelCodes: string[] | undefined | null;
};

const HotelFullDetailItem = ({ hotel, bookmarkHotelCodes }: Props) => {
  const { formatCurrency } = useCurrencyFormat();
  const { search } = useLocation();
  const services = React.useMemo(() => {
    return hotel.facilities?.map(f => {
      let icon = HotelServicesAndConveniences[f]?.icon;
      if (icon) {
        return {
          name: f,
          icon: icon,
        };
      }
      return {
        name: f,
        icon: 'ficon-confirmation-instant',
      };
    });
  }, [hotel]);

  const priceHintText = React.useMemo(() => {
    let request = getHotelSearchRequest(search);
    let night = moment(request?.checkoutDate).diff(moment(request?.checkinDate), 'day');
    let rooms = request?.rooms.length || 1;
    return [night > 1 ? `${night} nights` : '1 night', rooms > 1 ? `${rooms} rooms` : `${rooms} room`].join(', ');
  }, [search]);

  return (
    <div className="col-12" key={hotel.hotelCode}>
      <div className="border-top-light pb-30">
        <div className="text-14 px-5 py-5 mb-5" style={{ backgroundColor: '#f0f8ff' }}>
          {[hotel.prefectureName, hotel.areaName, hotel.cityName].filter(x => x).join(' > ')}
        </div>
        <div className="row x-gap-20 y-gap-20">
          <div className="col-md-auto">
            <div className="cardImage ratio ratio-1:1 w-250 md:w-1/1 rounded-4">
              <div className="cardImage__content">
                <div className="cardImage-slider rounded-4  custom_inside-slider">
                  <Swiper className="mySwiper" modules={[Navigation]} pagination={false} navigation={true}>
                    {hotel?.hotelImages?.length ? (
                      hotel?.hotelImages?.map((slide, i) => (
                        <SwiperSlide key={i}>
                          <img className="rounded-4 w-100  col-12 js-lazy" src={slide} alt="hotel" />
                        </SwiperSlide>
                      ))
                    ) : (
                      <SwiperSlide>
                        <img className="rounded-4  col-12 js-lazy" src={HOTEL_CONFIG.defaultImage} alt="hotel" />
                      </SwiperSlide>
                    )}
                  </Swiper>
                </div>
              </div>
              {/* End image */}

              <div className="cardImage__wishlist">
                <HotelBookmarkIcon hotelCode={hotel.hotelCode || ''} bookmarkHotelCodes={bookmarkHotelCodes} />
              </div>
            </div>
          </div>
          {/* End .col */}

          <div className="col-md" style={{ position: 'relative' }}>
            <div className="row gap-2">
              <div className="col">
                <h3 className="text-18 lh-16 fw-500 pr-50">
                  {hotel.hotelName}
                  <HotelJapaneseName name={hotel.originalName} addBefore="-" />
                </h3>
              </div>
              <div className="col-auto">
                <HotelRewardRating rate={hotel.reviewRating} />
              </div>
            </div>

            {hotel.starRating ? (
              <div className="pb-5">
                <HotelStar star={hotel.starRating || 0} />
              </div>
            ) : null}

            {(hotel as any).access ? (
              <div>
                <p className="text-14 pr-50">
                  <i className="icon-award text-16 mr-5 text-blue-1" />
                  {(hotel as any).access}
                </p>
              </div>
            ) : null}

            <div>
              <p className="text-14 pr-50">
                <i className="icon-location-2 text-16 mr-5 text-blue-1" />
                {hotel.address}
              </p>
            </div>

            <div className="row x-gap-10 y-gap-10 pt-5">
              <div className="col-md">
                <div className="text-14 lh-15 mt-5 fw-500">
                  {hotel.roomTypes?.join(', ')}
                  <div className="d-flex pt-10">{<BoardTypeList boardTypes={hotel.boardTypes || []} />}</div>
                </div>

                <div className="row x-gap-10 y-gap-10 pt-10">
                  <FacilityList facilities={services} />
                </div>
              </div>
              <div className="col-md-auto text-right md:text-left">
                <div className="text-14 text-light-1 ">{priceHintText}</div>
                <div className="text-22 lh-12 fw-600 mt-5">{formatCurrency(hotel?.cheapestPrice)}</div>
                <Link
                  target="_blank"
                  to={generateHotelDetailUrl(hotel.hotelName, hotel.hotelCode)}
                  className="button -md -dark-1 bg-blue-1 text-white mt-24"
                >
                  See Availability <div className="icon-arrow-top-right ml-15"></div>
                </Link>
              </div>
            </div>
          </div>
          {/* End .col-md */}
        </div>
      </div>
    </div>
  );
};

export default HotelFullDetailItem;
